// pwa.js - Everything that makes the dashboard behave like an installed app:
// the service worker, the install prompt, and push notification permission.
//
// The customer site is deliberately left alone. It is a website that wants to be
// crawled and shared, not an icon on a phone, so the worker is registered with
// scope /admin and controls nothing outside it even though one origin serves
// both.
//
// Every function here is safe to call in a browser that supports none of this.
// A phone with no push support, a desktop with notifications blocked and a
// private window all take the same path: the feature reports itself as
// unavailable and the dashboard carries on.
//
// LEGEND
//   1. isStandalone()       opened from a home screen icon rather than a tab
//   2. installPlatform()    how this browser installs, which decides the wording
//   3. watchInstallPrompt() capture the event Chrome fires once, very early
//   4. useInstall()         the hook the settings screen renders from
//   5. registerWorker()     register, and keep an eye on updates
//   6. push                 support, permission, subscribe, unsubscribe

import { useCallback, useEffect, useState } from "react";

////////// 1. STANDALONE //////////

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

////////// 2. PLATFORM //////////
// Chrome and Edge hand us an event we can turn into a one tap install. Safari
// never has, on either desktop or iOS, so those get instructions instead. There
// is no feature test for "will this browser fire beforeinstallprompt", which is
// why this reads the user agent, which it otherwise would not.

export function installPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const agent = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(agent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  if (/safari/i.test(agent) && !/chrome|chromium|crios|edg/i.test(agent)) return "safari";
  return "prompt";
}

////////// 3. THE CAPTURED EVENT //////////
// Chrome fires beforeinstallprompt once, shortly after load, and never again in
// that page's life. The settings screen is several taps away by then, so the
// event is caught at startup and held here until somebody asks for it.

let deferredPrompt = null;
const listeners = new Set();

const announce = () => { for (const listener of listeners) listener(); };

export function watchInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event;
    announce();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    announce();
  });
}

////////// 4. THE HOOK //////////

export function useInstall() {
  const [, setTick] = useState(0);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const listener = () => setTick((value) => value + 1);
    listeners.add(listener);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => { listeners.delete(listener); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  // The prompt can only be shown from inside a user gesture, and only once. If
  // they dismiss it, Chrome will not hand us another event this page load.
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return "unavailable";
    const event = deferredPrompt;
    deferredPrompt = null;
    announce();
    event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  }, []);

  return { canPrompt: Boolean(deferredPrompt), installed, platform: installPlatform(), promptInstall };
}

////////// 5. SERVICE WORKER //////////
// Scope matters more than it looks. The script sits at the root because a worker
// cannot claim a scope above its own path, and the scope is narrowed to /admin
// so a customer visiting the same origin is never served anything from a cache.

export async function registerWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;

  try {
    const registration = await navigator.serviceWorker.register("/admin-sw.js", { scope: "/admin" });

    // A new build is waiting behind the tab that is open. Take it immediately
    // rather than leaving somebody on last week's dashboard for a week.
    registration.addEventListener("updatefound", () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener("statechange", () => {
        if (installing.state === "installed" && navigator.serviceWorker.controller) {
          installing.postMessage("skip-waiting");
        }
      });
    });

    return registration;
  } catch {
    // An unsupported browser, a private window, or an origin without TLS.
    return null;
  }
}

////////// 6. PUSH //////////

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const pushPermission = () => (pushSupported() ? Notification.permission : "unsupported");

// A push subscription is a per browser, per device thing. This is the endpoint
// this browser currently holds, if any, which is how the settings screen knows
// whether to offer "turn on" or "turn off".
export async function currentSubscription() {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration("/admin");
  if (!registration) return null;
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? subscription.toJSON() : null;
}

// The application server key travels as base64url and has to arrive as bytes.
function decodeKey(base64url) {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(base64url.length / 4) * 4, "=");
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export async function subscribeToPush(publicKey) {
  if (!pushSupported()) throw new Error("This browser cannot receive push notifications.");
  if (!publicKey) throw new Error("Push is not configured on the server yet.");

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked for this site. Turn them back on in your browser settings for this site, then try again."
        : "Notifications were not allowed."
    );
  }

  const registration = (await navigator.serviceWorker.getRegistration("/admin")) || (await registerWorker());
  if (!registration) throw new Error("The app could not start its background worker.");
  await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeKey(publicKey),
  });
  return subscription.toJSON();
}

export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker?.getRegistration("/admin");
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return null;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
}
